/* eslint-disable max-lines-per-function */
import { Button, Badge, ScrollArea, ScrollBar } from '@e2n/ui';
import { useAnlageassistentFormContext } from '@/domain/mitarbeiter/features/anlageassistent/context/AnlageassistentFormContext';
import { useAnlageassistentContext } from '@/domain/mitarbeiter/features/anlageassistent/context/useAnlageassistentContext';
import { Arbeitsbereich } from '@/domain/mitarbeiter/features/anlageassistent/entities/Arbeitsbereich';
import { useArbeitsbereiche } from '@/domain/mitarbeiter/features/anlageassistent/hooks/useArbeitsbereiche/useArbeitsbereiche';
import { useErfahrungslevelOptions } from '@/domain/mitarbeiter/features/anlageassistent/hooks/useErfahrungslevelOptions';
import { useStandortContext } from '@/features/standortContext/StandortContext';
import { faEllipsis, faPenToSquare } from '@fortawesome/pro-regular-svg-icons';
import { faCircle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';

export function Arbeitsbereiche() {
  const { t } = useTranslation();
  const { ctx } = useAnlageassistentContext();
  const form = useAnlageassistentFormContext();
  const { currentStandort } = useStandortContext();
  const { arbeitsbereiche } = useArbeitsbereiche(currentStandort.id);

  const data = form.state.values;

  const primaererArbeitsbereich = arbeitsbereiche.find(
    (ab) => ab.id === data.person.arbeitsbereiche.primaererArbeitsbereich,
  );

  const sekundaereArbeitsbereiche = arbeitsbereiche.filter((ab) =>
    data.person.arbeitsbereiche.sekundaereArbeitsbereiche.includes(ab.id),
  );

  return (
    <div className="flex flex-col gap-2">
      <div className="flex gap-2 items-center justify-between">
        <h2 className="text-neutrals-900 text-base font-bold leading-6 wrap-break-word">
          {t('domain.mitarbeiter.create.steps.person.arbeitsbereiche.title')}
        </h2>
        <Button
          variant={'icon'}
          onClick={() => ctx.navigateToSubStep('person.arbeitsbereiche')}>
          <FontAwesomeIcon
            icon={faPenToSquare}
            className="text-primary-blue-600"
          />
        </Button>
      </div>
      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-1">
          <span className="text-neutrals-800 text-sm font-normal leading-5">
            {t(
              'domain.mitarbeiter.create.steps.person.arbeitsbereiche.form.primaererArbeitsbereich.label',
            )}
          </span>

          {primaererArbeitsbereich ? (
            <ArbeitsbereichBadge arbeitsbereich={primaererArbeitsbereich} />
          ) : (
            <span className="text-neutrals-900 text-sm font-semibold leading-5">
              {'-'}
            </span>
          )}
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-neutrals-800 text-sm font-normal leading-5">
            {t(
              'domain.mitarbeiter.create.steps.person.arbeitsbereiche.form.erfahrungslevel.label',
            )}
          </span>

          {data.person.arbeitsbereiche.erfahrungslevel ? (
            <ErfahrungslevelBadge
              erfahrungslevel={data.person.arbeitsbereiche.erfahrungslevel}
            />
          ) : (
            <span className="text-neutrals-900 text-sm font-semibold leading-5">
              {'-'}
            </span>
          )}
        </div>
        {currentStandort.qualifikationenEnabled && (
          <div className="flex flex-col gap-1">
            <span className="text-neutrals-800 text-sm font-normal leading-5">
              {t(
                'domain.mitarbeiter.create.steps.person.arbeitsbereiche.form.sekundaereArbeitsbereiche.label',
              )}
            </span>
            <ScrollArea>
              <div className="flex gap-2">
                {sekundaereArbeitsbereiche.length > 0 ? (
                  sekundaereArbeitsbereiche.map((ab) => (
                    <ArbeitsbereichBadge key={ab.id} arbeitsbereich={ab} />
                  ))
                ) : (
                  <span className="text-neutrals-900 text-sm font-semibold leading-5">
                    {'-'}
                  </span>
                )}
              </div>
              <ScrollBar orientation="horizontal" />
            </ScrollArea>
          </div>
        )}
      </div>
    </div>
  );
}

function ArbeitsbereichBadge({
  arbeitsbereich,
}: {
  arbeitsbereich: Arbeitsbereich;
}) {
  return (
    <Badge
      variant={'secondary'}
      className="flex flex-row gap-2 items-center w-fit rounded-full p-3 bg-neutrals-200 max-w-44">
      <FontAwesomeIcon
        icon={faCircle}
        color={arbeitsbereich.getBorderColor()}
      />
      <span className="text-neutrals-900 text-sm font-semibold leading-5 truncate">
        {arbeitsbereich.name}
      </span>
    </Badge>
  );
}

function ErfahrungslevelBadge({
  erfahrungslevel,
}: {
  erfahrungslevel: string;
}) {
  const options = useErfahrungslevelOptions();
  return (
    <Badge
      variant={'secondary'}
      className="flex flex-row gap-2 items-center w-fit rounded-full p-3 bg-neutrals-200">
      <FontAwesomeIcon
        icon={
          options.find((o) => o.key === erfahrungslevel)?.icon || faEllipsis
        }
      />
      <span className="text-neutrals-900 text-sm font-semibold leading-5">
        {options.find((o) => o.key === erfahrungslevel)?.name ||
          erfahrungslevel}
      </span>
    </Badge>
  );
}
